import { useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '@/config/msal';
import { getSupabaseClient } from '@/config/supabase';
import { useAuthStore } from '@/stores/useAuthStore';
import { fetchManagerBySupabaseUserId, finalizeProfile } from '@/services/auth';
import {
  createOrganizationAndOwner,
  acceptOrganizationInvite,
} from '@/services/organizations';

/**
 * Extract a human-readable message from a thrown value. Supabase RPC failures
 * reject with a `PostgrestError` — a plain object ({ message, details, hint,
 * code }), NOT an `Error` instance — so `err instanceof Error` misses them and
 * the real cause (e.g. "Could not find the function ...") gets swallowed.
 */
function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error) return err.message;
  if (err && typeof err === 'object' && 'message' in err) {
    const { message } = err as { message?: unknown };
    if (typeof message === 'string' && message.trim() !== '') return message;
  }
  return fallback;
}

/**
 * Unified auth surface. Both entry methods (Microsoft SSO and Supabase
 * email/password) resolve to a real manager profile and coexist side by side.
 */
export function useAuth() {
  const { instance } = useMsal();
  const { manager, authMethod, isAuthenticated, isLoading, noProfile, error } = useAuthStore();

  const loginWithMicrosoft = useCallback(async () => {
    await instance.loginRedirect(loginRequest);
  }, [instance]);

  const loginWithEmail = useCallback(async (email: string, password: string) => {
    const store = useAuthStore.getState();
    store.setLoading(true);
    store.setError(null);
    const supabase = getSupabaseClient();
    const { data, error: authError } = await supabase.auth.signInWithPassword({ email, password });
    if (authError) {
      store.setError(authError.message);
      return;
    }
    if (!data.user) {
      store.setError('Sign in failed');
      return;
    }
    try {
      const profile = await fetchManagerBySupabaseUserId(data.user.id);
      if (!profile) {
        store.setNoProfile('supabase');
        return;
      }
      store.setManager(await finalizeProfile(profile), 'supabase');
    } catch (err) {
      store.setError(err instanceof Error ? err.message : 'Failed to load profile');
    }
  }, []);

  const signUp = useCallback(
    async (params: {
      email: string;
      password: string;
      organizationName: string;
      displayName?: string;
    }) => {
      const store = useAuthStore.getState();
      store.setLoading(true);
      store.setError(null);
      const supabase = getSupabaseClient();
      const { data, error: authError } = await supabase.auth.signUp({
        email: params.email,
        password: params.password,
      });
      if (authError) {
        store.setError(authError.message);
        return;
      }
      // Email confirmation is expected to be OFF, so a session should exist immediately.
      if (!data.session || !data.user) {
        store.setError(
          'Account created but no active session. Email confirmation must be disabled to create an organization.',
        );
        return;
      }
      try {
        const profile = await createOrganizationAndOwner({
          name: params.organizationName,
          displayName: params.displayName?.trim() || params.email,
        });
        store.setManager(await finalizeProfile(profile), 'supabase');
      } catch (err) {
        store.setError(errorMessage(err, 'Failed to create organization'));
      }
    },
    [],
  );

  const acceptInvite = useCallback(
    async (params: {
      email: string;
      password: string;
      displayName: string;
      token: string;
    }) => {
      const store = useAuthStore.getState();
      store.setLoading(true);
      store.setError(null);
      const supabase = getSupabaseClient();

      // The invitee may be brand new (sign up) or already have an account (sign in).
      // Try to create the account; if it already exists, fall back to signing in with
      // the password they entered. Either way we need a live session before redeeming.
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: params.email,
        password: params.password,
      });
      let session = signUpData.session;
      if (signUpError) {
        const alreadyRegistered = /already|registered|exists/i.test(signUpError.message);
        if (!alreadyRegistered) {
          store.setError(signUpError.message);
          return;
        }
        const { data: signInData, error: signInError } =
          await supabase.auth.signInWithPassword({
            email: params.email,
            password: params.password,
          });
        if (signInError) {
          store.setError(
            'That email already has an account. Enter its password to accept the invite.',
          );
          return;
        }
        session = signInData.session;
      }

      if (!session) {
        store.setError(
          'Account created but no active session. Email confirmation must be disabled to accept invites.',
        );
        return;
      }

      try {
        const profile = await acceptOrganizationInvite(params.token, params.displayName);
        store.setManager(await finalizeProfile(profile), 'supabase');
      } catch (err) {
        store.setError(errorMessage(err, 'Failed to accept invitation'));
      }
    },
    [],
  );

  /**
   * Kick off the forgot-password flow: emails the user a recovery link that lands
   * on /reset-password. Only email/password accounts have a password to reset;
   * Microsoft SSO users manage credentials in Azure AD. Supabase intentionally
   * does NOT reveal whether the address exists, so success here just means the
   * request was accepted — callers should show a neutral "check your email" state.
   * Returns an error string instead of writing the global auth store so a failed
   * reset request doesn't leak onto the login screen.
   */
  const sendPasswordReset = useCallback(
    async (email: string): Promise<{ error?: string }> => {
      const supabase = getSupabaseClient();
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) return { error: error.message };
      return {};
    },
    [],
  );

  /**
   * Complete the reset by setting a new password on the recovery session that the
   * emailed link established (Supabase installs it from the URL on load). Requires
   * an active session; if the link expired there won't be one and Supabase rejects.
   */
  const updatePassword = useCallback(
    async (newPassword: string): Promise<{ error?: string }> => {
      const supabase = getSupabaseClient();
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) return { error: error.message };
      return {};
    },
    [],
  );

  const logout = useCallback(async () => {
    const supabase = getSupabaseClient();
    await supabase.auth.signOut().catch(() => {});
    useAuthStore.getState().logout();
    // Anyone with a cached MSAL account signed in via Microsoft — even though they now
    // also hold a Supabase session post token-exchange — needs an MSAL logout too, or
    // the next load would silently re-exchange and sign them back in.
    if (instance.getAllAccounts().length > 0) {
      await instance.logoutRedirect();
    }
  }, [instance]);

  return {
    manager,
    authMethod,
    isAuthenticated,
    isLoading,
    noProfile,
    error,
    isSuperAdmin: manager?.is_super_admin ?? false,
    organizationIds: manager?.organization_ids ?? [],
    loginWithMicrosoft,
    loginWithEmail,
    signUp,
    acceptInvite,
    sendPasswordReset,
    updatePassword,
    logout,
  };
}
